<template>
  <Combobox v-model="model" v-slot="{ open }">
    <ComboboxLabel
      v-if="label"
      class="mb-2 font-semibold inline-block text-slate-700"
      >{{ label }}</ComboboxLabel
    >
    <div class="relative">
      <ComboboxInput
        @change="query = $event.target.value"
        :display-value="
          (value) =>
            options.find((option) => option.value === value)?.label || ''
        "
        placeholder="Start typing..."
        class="flex items-center justify-between bg-white border-2 border-primary rounded-lg w-full text-left h-10 px-3 outline-none"
      />
      <div class="absolute inset-y-0 right-0 flex items-center pr-3">
        <button
          v-if="model"
          type="button"
          @click="emit('update:modelValue', '')"
          class="mr-3"
          :tabindex="-1"
        >
          <font-awesome-icon icon="times" class="text-primary-darker" />
        </button>
        <ComboboxButton
          ><font-awesome-icon
            icon="fa-chevron-down"
            aria-hidden="true"
            class="text-primary-darker transition-transform"
            :class="{ 'rotate-180': open }"
        /></ComboboxButton>
      </div>
    </div>
    <TransitionRoot
      @after-leave="query = ''"
    >
      <div class="relative">
        <ComboboxOptions
          class="z-20 max-h-80 overflow-y-scroll border-2 border-primary bg-white absolute w-full top-1 rounded-lg"
        >
          <ComboboxOption
            v-if="filteredOptions.length > 0"
            v-for="option in filteredOptions"
            :key="option.value"
            :value="option.value"
            v-slot="{ active, selected }"
            class="group"
          >
            <div
              class="h-10 px-3 flex items-center border-b group-last:border-b-0"
              :class="
                selected
                  ? 'bg-primary text-white'
                  : active
                  ? 'bg-slate-100'
                  : 'bg-white'
              "
            >
              {{ option.label }}
            </div>
          </ComboboxOption>
          <p v-else class="h-10 px-3 flex items-center">Nothing found.</p>
        </ComboboxOptions>
      </div>
    </TransitionRoot>
  </Combobox>
</template>

<script setup>
import { ref, computed } from "vue";
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxLabel,
  ComboboxButton,
  TransitionRoot,
} from "@headlessui/vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  options: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const model = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const query = ref("");

const filteredOptions = computed(() =>
  query.value === ""
    ? props.options
    : props.options.filter((option) =>
        option.label.toLowerCase().includes(query.value.toLowerCase())
      )
);
</script>
